import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { MarketplaceCartItemWithProduct } from "@redotech/redo-model/recommerce/marketplace-cart";
import { Button } from "@redotech/redo-web/components/ui/button";
import { Input } from "@redotech/redo-web/components/ui/input";
import { Label } from "@redotech/redo-web/components/ui/label";
import { Separator } from "@redotech/redo-web/components/ui/separator";
import { Flex } from "@redotech/redo-web/flex";
import { ImageDisplayWithPlaceholder } from "@redotech/redo-web/image-display-with-placeholder";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useCart } from "../../context";
import { CartItem } from "../../context/cart-context";
import * as checkoutPageStyles from "./checkout-page.module.css";

export interface CheckoutPageProps {
  onBack?: () => void;
  onOrderComplete?: (orderDetails: OrderDetails) => void;
}

export interface CustomerInfo {
  email: string;
}

export interface OrderDetails {
  customerInfo: CustomerInfo;
  items: CartItem[];
  totalPrice: number;
}

interface OrderSummaryProps {
  items: readonly MarketplaceCartItemWithProduct[];
  totalPrice: number;
}

const OrderSummary = memo(function OrderSummary({
  items,
  totalPrice,
}: OrderSummaryProps) {
  return (
    <div className={checkoutPageStyles.orderSummaryCard}>
      <Text className={checkoutPageStyles.sectionTitle}>Order Summary</Text>

      <Flex className={checkoutPageStyles.itemsList} dir="column">
        {items.map((item) => {
          const featuredImage = item.product.images[0];
          const itemPrice = item.variant.price || 0;
          const itemTotal = itemPrice * item.quantity;
          const itemId = `${item.productId}-${item.variantId}`;

          return (
            <div className={checkoutPageStyles.orderItem} key={itemId}>
              <div className={checkoutPageStyles.itemDetails}>
                {featuredImage ? (
                  <ImageDisplayWithPlaceholder
                    alt={featuredImage.altText || item.product.name}
                    className={checkoutPageStyles.itemImage}
                    size="xs"
                    src={featuredImage.url}
                  />
                ) : (
                  <div className={checkoutPageStyles.imagePlaceholder}>
                    <Text className={checkoutPageStyles.placeholderText}>
                      No Image
                    </Text>
                  </div>
                )}

                <div className={checkoutPageStyles.itemInfo}>
                  <Text className={checkoutPageStyles.itemName}>
                    {item.product.name}
                  </Text>
                  <Text className={checkoutPageStyles.itemQuantity}>
                    Qty: {item.quantity} × ${itemPrice.toFixed(2)}
                  </Text>
                </div>
              </div>

              <Text className={checkoutPageStyles.itemPrice}>
                ${itemTotal.toFixed(2)}
              </Text>
            </div>
          );
        })}

        <Separator className={checkoutPageStyles.divider} />

        <div className={checkoutPageStyles.totalRow}>
          <Text className={checkoutPageStyles.totalLabel}>Total</Text>
          <Text className={checkoutPageStyles.totalAmount}>
            ${totalPrice.toFixed(2)}
          </Text>
        </div>
      </Flex>
    </div>
  );
});

export const CheckoutPage = memo(function CheckoutPage({
  onBack,
  onOrderComplete,
}: CheckoutPageProps) {
  const { items, totalPrice, cartId } = useCart();
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );

  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({ email: "" });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCustomerInfoChange = useHandler(
    (field: keyof CustomerInfo, value: string) => {
      setCustomerInfo((prev) => ({ ...prev, [field]: value }));
    },
  );

  const handleSubmitOrder = useHandler(async () => {
    if (!customerInfo.email) {
      alert("Please enter your email address");
      return;
    }

    if (items.length === 0) {
      alert("Your cart is empty");
      return;
    }

    if (!cartId) {
      alert("Cart not found. Please refresh and try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response =
        await marketplaceRpcClient.createMarketplaceCheckoutSession({
          cartId,
          customerEmail: customerInfo.email,
          currency: "USD",
        });

      // Redirect to Stripe checkout
      window.location.href = response.checkoutUrl;
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      alert("Failed to create checkout session. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  });

  if (items.length === 0) {
    return (
      <div
        className={checkoutPageStyles.emptyState}
        style={{ backgroundColor: "#ffffff", color: "#111827" }}
      >
        <Text className={checkoutPageStyles.emptyTitle}>
          Your cart is empty
        </Text>
        <Text className={checkoutPageStyles.emptyDescription}>
          Add some items to your cart before proceeding to checkout.
        </Text>
        {onBack && (
          <Button onClick={onBack} size="lg">
            Continue Shopping
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={checkoutPageStyles.pageContainer}
      style={{ backgroundColor: "#ffffff", color: "#111827" }}
    >
      <div className={checkoutPageStyles.pageHeader}>
        <h1 className={checkoutPageStyles.pageTitle}>Checkout</h1>
        {onBack && (
          <Button onClick={onBack} size="sm" variant="outline">
            ← Back to Cart
          </Button>
        )}
      </div>

      <div className={checkoutPageStyles.contentGrid}>
        <div className={checkoutPageStyles.mainContent}>
          <div className={checkoutPageStyles.formCard}>
            <h2 className={checkoutPageStyles.sectionTitle}>
              Customer Information
            </h2>
            <div className={checkoutPageStyles.formGroup}>
              <Label htmlFor="email">Email Address</Label>
              <Input
                className={checkoutPageStyles.emailInput}
                id="email"
                onChange={(e) =>
                  handleCustomerInfoChange("email", e.target.value)
                }
                placeholder="your@email.com"
                type="email"
                value={customerInfo.email}
              />
              <Text className={checkoutPageStyles.helperText}>
                We'll use this email to send you order confirmation and updates.
              </Text>
            </div>
          </div>

          <div className={checkoutPageStyles.formCard}>
            <h2 className={checkoutPageStyles.sectionTitle}>Payment Method</h2>
            <div className={checkoutPageStyles.paymentInfo}>
              <div className={checkoutPageStyles.paymentIcon}>
                <svg
                  className={checkoutPageStyles.stripeIcon}
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 7C3 5.89543 3.89543 5 5 5H19C20.1046 5 21 5.89543 21 7V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V7Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path d="M3 10H21" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M7 15H10"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div className={checkoutPageStyles.paymentText}>
                <Text className={checkoutPageStyles.paymentDescription}>
                  Secure payment powered by Stripe
                </Text>
                <Text className={checkoutPageStyles.paymentMethods}>
                  Accepts all major credit cards, debit cards, and digital
                  wallets
                </Text>
              </div>
            </div>
          </div>

          <Button
            className={checkoutPageStyles.submitButton}
            disabled={
              !customerInfo.email ||
              items.length === 0 ||
              !cartId ||
              isSubmitting
            }
            onClick={handleSubmitOrder}
            size="lg"
          >
            {isSubmitting
              ? "Redirecting to payment..."
              : `Proceed to Payment • $${totalPrice.toFixed(2)}`}
          </Button>
        </div>

        <div className={checkoutPageStyles.sidebarContent}>
          <OrderSummary items={items} totalPrice={totalPrice} />
        </div>
      </div>
    </div>
  );
});
